import { useRef, useEffect, forwardRef } from 'react';
import PropTypes from 'prop-types';
import { cn } from 'Utils/cn';
import { cva } from 'class-variance-authority';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import { useMergeRefs } from '@floating-ui/react';
import FloatLabel from '../FloatLabel/FloatLabel';
import ButtonClear from '../ButtonClear/ButtonClear';

const textareaVariants = cva(
    'flex w-full text-dark-blue rounded-md border border-gray-200 py-2.5 bg-white px-3.5 shadow-sm placeholder:text-dark-blue/50 focus-visible:outline-none focus-visible:ring-2 ring-primary/20 focus-visible:border-primary disabled:cursor-not-allowed disabled:opacity-50 md:text-sm max-h-[230px] resize-none scrollbar-thin',
    {
        variants: {
            status: {
                error: 'border-danger',
                default: '',
            },
            compact: {
                true: 'h-11 transition-[border-color,color,box-shadow]',
                false: 'min-h-[80px] transition-all',
            },
        },
        defaultVariants: {
            status: 'default',
            compact: false,
        },
    },
);

const Textarea = forwardRef(function Textarea(props, ref) {
    const {
        className,
        label,
        error,
        value,
        disableAutoResize,
        wrapperClassName,
        enableClear,
        onClear,
        compact = false,
        ...rest
    } = props;
    const innerRef = useRef();
    const textareaRef = useMergeRefs([innerRef, ref]);

    useEffect(() => {
        if (disableAutoResize) return;
        if (!innerRef.current) return;

        const el = innerRef.current;

        if (compact) {
            el.style.height = '';
            if (el.scrollHeight > el.clientHeight) {
                el.style.height = el.scrollHeight + 'px';
            }
            return;
        }

        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
    }, [value, disableAutoResize, compact]);

    const hasError = !!error;

    return (
        <div className={cn('relative group', wrapperClassName)}>
            <FloatLabel label={label} hasError={hasError} />

            <div className="relative">
                <textarea
                    className={cn(
                        textareaVariants({ status: hasError ? 'error' : 'default', compact, className }),
                        enableClear && 'pr-9',
                    )}
                    value={value}
                    ref={textareaRef}
                    {...rest}
                />
                {enableClear && (
                    <span className="absolute right-2 top-2.5">
                        <ButtonClear onClear={onClear} />
                    </span>
                )}
            </div>
            <ErrorMessage message={error} />
        </div>
    );
});

export default Textarea;

Textarea.propTypes = {
    className: PropTypes.string,
    wrapperClassName: PropTypes.string,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    value: PropTypes.string,
    disableAutoResize: PropTypes.bool,
    enableClear: PropTypes.bool,
    onClear: PropTypes.func,
    compact: PropTypes.bool,
};
